import { useEffect } from 'react';
import type { Path } from 'react-hook-form';

import SOURCE_TYPE, { WIZARD_STEP } from '../SourceCreate.constants';
import {
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';
import type { UseWizardDataProviderPresetParams } from './SourceCreateDialog.types';

/**
 * Custom hook to pre-populate data provider selections when entering wizard step 2.
 *
 * Business rule: After selecting a source in step 1, we pre-populate the data providers
 * in step 2:
 * - Microsoft sources: All 4 providers selected by default (O365, Defender, Entra ID Graph, Entra ID O365)
 * - AWS S3: Only the selected provider
 * - Google Storage: Only the selected provider
 *
 * The 300ms timeout ensures the form schema is fully loaded before setting values.
 *
 * @param params - Mode, step, selected source, schema, and setValue function
 */
export const useWizardDataProviderPreset = ({
  mode,
  currentStep,
  wizardSelectedSource,
  connectionInputSchema,
  setValue
}: UseWizardDataProviderPresetParams): void => {
  useEffect(() => {
    if (
      mode === SourceDialogMode.New &&
      currentStep === WIZARD_STEP.CONFIGURE_SOURCE &&
      wizardSelectedSource &&
      connectionInputSchema &&
      Object.keys(connectionInputSchema).length > 0
    ) {
      // Wait for the form schema to load before setting data provider values
      const timer = setTimeout(() => {
        if (wizardSelectedSource.source_type_id) {
          const sourceTypeId = wizardSelectedSource.source_type_id;
          const dataProvider = wizardSelectedSource.data_provider;

          // Microsoft: Select all 4 data providers by default
          if (Number(sourceTypeId) === SOURCE_TYPE.MICROSOFT) {
            setValue('data_provider.O365.selected', true, {
              shouldValidate: true,
              shouldDirty: true
            });
            setValue('data_provider.MicrosoftDefenderUnified.selected', true, {
              shouldValidate: true,
              shouldDirty: true
            });
            setValue('data_provider.MicrosoftEntraID.GraphAPI.selected', true, {
              shouldValidate: true,
              shouldDirty: true
            });
            setValue('data_provider.MicrosoftEntraID.O365API.selected', true, {
              shouldValidate: true,
              shouldDirty: true
            });
          } else if (Number(sourceTypeId) === SOURCE_TYPE.AWS_S3) {
            // AWS S3: Select only the chosen data provider
            setValue(
              `data_provider.${dataProvider}.selected` as Path<SourceCreateDynamicFormModel>,
              true
            );
          } else if (Number(sourceTypeId) === SOURCE_TYPE.GOOGLE_STORAGE) {
            // Google Storage: Select only the chosen data provider
            setValue(
              `data_provider.${dataProvider}.selected` as Path<SourceCreateDynamicFormModel>,
              true
            );
          }
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    mode,
    currentStep,
    wizardSelectedSource,
    connectionInputSchema,
    setValue
  ]);
};
